import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, SendHorizontal, Phone } from "lucide-react";
import { toast } from "sonner";
import { imageUrl } from "@/lib/lamka";
import { listMessages, sendMessage, markRead, getChatContact, timeLabel, normalizeMessage } from "@/lib/chat";

const byTime = (a, b) => new Date(a.created_at) - new Date(b.created_at);

// Server list wins, but keep local messages (sending/just-sent/failed) the server hasn't returned yet.
// Each server message can confirm at most one local message, and only one created around/after it was sent.
const merge = (server, local) => {
  const ids = new Set(server.map((m) => m.id));
  const claimed = new Set(local.map((m) => m.serverId).filter(Boolean));
  const extra = local.filter((m) => {
    if (!m.local) return false;
    if (ids.has(m.serverId || m.id)) return false;
    if (m.status !== "sent") return true; // still sending or failed: only its own POST result removes it
    const sentAt = new Date(m.created_at) - 5000;
    const echo = server.find((s) => !claimed.has(s.id) && s.sender_id === m.sender_id && s.text === m.text && new Date(s.created_at) >= sentAt && new Date(s.created_at) - sentAt < 125000);
    if (echo) {
      claimed.add(echo.id);
      return false;
    }
    return true;
  });
  return [...server, ...extra].sort(byTime);
};

// Outgoing messages per conversation survive switching chats while a send is in flight.
const outbox = new Map();
const listeners = new Set();
const patchOutbox = (convId, tempId, fn) => {
  const list = outbox.get(convId) || [];
  const hit = list.find((x) => x.id === tempId);
  outbox.set(convId, list.map((x) => (x.id === tempId ? fn(x) : x)).filter((x) => x.status !== "sent"));
  if (hit) listeners.forEach((l) => l(convId, tempId, fn(hit)));
};

export const ChatThread = ({ conv, userId, onActivity }) => {
  const [messages, setMessages] = useState(() => outbox.get(conv.id) || []);
  const [text, setText] = useState("");
  const [contact, setContact] = useState(null);
  const endRef = useRef(null);
  const reqSeq = useRef(0);
  const iAmSeller = conv.seller_id === userId;
  const other = iAmSeller ? conv.buyer_name : conv.seller_name;

  const load = useCallback(() => {
    const seq = ++reqSeq.current;
    return listMessages(conv.id)
      .then((list) => { if (seq === reqSeq.current) setMessages((cur) => merge(list, cur)); })
      .catch(() => {});
  }, [conv.id]);

  useEffect(() => {
    setMessages(outbox.get(conv.id) || []);
    setContact(null);
    load();
    markRead(conv.id).then(onActivity);
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conv.id, load]);

  useEffect(() => endRef.current?.scrollIntoView({ block: "end" }), [messages.length]);

  // Apply status changes from sends started before this thread was (re)opened
  useEffect(() => {
    const l = (convId, tempId, next) => {
      if (convId === conv.id) setMessages((m) => m.map((x) => (x.id === tempId ? next : x)));
    };
    listeners.add(l);
    return () => listeners.delete(l);
  }, [conv.id]);

  const update = (tempId, fn) => {
    const inOutbox = (outbox.get(conv.id) || []).some((x) => x.id === tempId);
    patchOutbox(conv.id, tempId, fn);
    if (!inOutbox) setMessages((m) => m.map((x) => (x.id === tempId ? fn(x) : x)));
  };

  const deliver = async (tempId, body) => {
    const convId = conv.id;
    reqSeq.current++; // ignore any poll that started before this send
    update(tempId, (x) => ({ ...x, status: "sending" }));
    try {
      const raw = await sendMessage(convId, body);
      const saved = normalizeMessage(raw || {}, { text: body, sender_id: userId });
      update(tempId, (x) => ({ ...x, ...saved, id: tempId, serverId: saved.id, text: saved.text || body, status: "sent" }));
      onActivity();
      load();
    } catch (err) {
      update(tempId, (x) => ({ ...x, status: "failed" }));
      toast.error(err?.response?.data?.detail || "Message not sent. Tap it to retry.");
      getChatContact(convId).then(setContact).catch(() => {});
    }
  };

  const send = (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    const tempId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const msg = { id: tempId, local: true, sender_id: userId, text: body, created_at: new Date().toISOString(), status: "sending" };
    outbox.set(conv.id, [...(outbox.get(conv.id) || []), msg]);
    setMessages((m) => [...m, msg]);
    setText("");
    deliver(tempId, body);
  };

  return (
    <div data-testid="chat-thread" className="flex flex-col h-full min-h-0">
      <div className="flex items-center gap-3 px-4 sm:px-6 py-4 border-b border-[#C5A059]/25 bg-white">
        <Link to="/messages" data-testid="chat-back-button" aria-label="Back" className="lg:hidden p-1 -ml-1"><ArrowLeft className="h-5 w-5" /></Link>
        <Link to={conv.listing_id ? `/product/${conv.listing_id}` : "/messages"} data-testid="chat-listing-link" className="flex items-center gap-3 min-w-0 group">
          <div className="h-12 w-9 bg-[#F5EFE6] overflow-hidden shrink-0">
            {conv.listing_image && <img src={imageUrl(conv.listing_image)} alt="" className="h-full w-full object-cover" />}
          </div>
          <div className="min-w-0">
            <p data-testid="chat-other-name" className="font-display text-lg leading-tight truncate">{other || "Lamka member"}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#B38F46] truncate group-hover:underline">{conv.listing_title}</p>
          </div>
        </Link>
      </div>

      <div data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 sm:px-6 py-6 space-y-3 bg-[#FBF9F5]">
        {messages.length === 0 && <p className="text-center text-sm text-[#71717A] py-10">Start the conversation — ask about size, colour or delivery.</p>}
        {messages.map((m) => {
          const mine = m.local || m.sender_id === userId;
          return m.system ? (
            <p key={m.id} data-testid="chat-system-message" className="text-center font-mono text-[10px] uppercase tracking-[0.18em] text-[#71717A] py-2 px-6">{m.text}</p>
          ) : (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div
                data-testid={mine ? "chat-message-mine" : "chat-message-theirs"}
                data-status={m.status || "sent"}
                onClick={m.status === "failed" ? () => deliver(m.id, m.text) : undefined}
                className={`max-w-[78%] px-4 py-2.5 text-sm whitespace-pre-wrap break-words transition-opacity ${mine ? "bg-[#0F0F0F] text-white" : "bg-white border border-[#0F0F0F]/10"} ${m.status === "sending" ? "opacity-60" : ""} ${m.status === "failed" ? "cursor-pointer ring-1 ring-red-400" : ""}`}
              >
                {m.text}
                <span className={`block text-[10px] font-mono mt-1 ${mine ? "text-[#C5A059]/80" : "text-[#71717A]"}`}>
                  {m.status === "sending" ? "Sending…" : m.status === "failed" ? <span data-testid="chat-message-failed" className="text-red-300">Not sent · tap to retry</span> : timeLabel(m.created_at)}
                </span>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {contact && (contact.phone || contact.upi) && (
        <div data-testid="chat-contact-card" className="px-4 sm:px-6 py-3 bg-[#0F0F0F] text-white text-sm flex flex-wrap items-center gap-x-6 gap-y-1">
          <span className="eyebrow">Continue directly</span>
          {contact.phone && <a href={`tel:${contact.phone}`} className="flex items-center gap-2 hover:text-[#C5A059]"><Phone className="h-4 w-4" />{contact.phone}</a>}
          {contact.upi && <span className="font-mono text-xs">UPI: {contact.upi}</span>}
        </div>
      )}

      <form onSubmit={send} className="flex items-end gap-2 px-4 sm:px-6 py-4 border-t border-[#C5A059]/25 bg-white">
        <textarea
          data-testid="chat-input"
          rows={1}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) send(e); }}
          placeholder="Write a message…"
          className="flex-1 resize-none bg-[#FBF9F5] border border-[#0F0F0F]/15 focus:border-[#C5A059] outline-none px-4 py-3 text-sm max-h-32"
        />
        <button data-testid="chat-send-button" type="submit" disabled={!text.trim()} aria-label="Send" className="h-12 w-12 shrink-0 bg-[#0F0F0F] text-[#C5A059] flex items-center justify-center disabled:opacity-40 transition-transform active:scale-95">
          <SendHorizontal className="h-5 w-5" strokeWidth={1.5} />
        </button>
      </form>
    </div>
  );
};
